import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Autocomplete,
  Stack,
} from '@mui/material'
import {
  Sync as SyncIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  WhatsApp as WhatsAppIcon,
  Receipt as ReceiptIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CardIcon,
  Payments as PaymentsIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Settings as SettingsIcon,
  Block as VoidIcon,
  Visibility as ViewIcon,
  OpenInNew as OpenInNewIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import {
  paymentService,
  PaymentDashboardMetrics,
  UnpaidInvoiceItem,
  PaymentRecordItem,
  RazorpaySyncResult,
} from '../services/paymentService'

export const PaymentsDashboardPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  // Tab State
  const [currentTab, setCurrentTab] = useState<number>(0)

  // Metrics State
  const [metrics, setMetrics] = useState<PaymentDashboardMetrics | null>(null)
  const [loadingMetrics, setLoadingMetrics] = useState<boolean>(false)

  // Payments Table State
  const [payments, setPayments] = useState<PaymentRecordItem[]>([])
  const [loadingPayments, setLoadingPayments] = useState<boolean>(false)
  const [totalPayments, setTotalPayments] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(20)

  // Filter States
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [filterMethod, setFilterMethod] = useState<string>('all')
  const [filterStatus, setFilterStatus] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  // Unpaid Invoices State (Tab 1)
  const [unpaidInvoices, setUnpaidInvoices] = useState<UnpaidInvoiceItem[]>([])
  const [loadingUnpaid, setLoadingUnpaid] = useState<boolean>(false)
  const [unpaidSearch, setUnpaidSearch] = useState<string>('')

  // Modal States
  const [syncModalOpen, setSyncModalOpen] = useState<boolean>(false)
  const [syncLoading, setSyncLoading] = useState<boolean>(false)
  const [syncResult, setSyncResult] = useState<RazorpaySyncResult | null>(null)

  const [recordModalOpen, setRecordModalOpen] = useState<boolean>(false)
  const [recordingPayment, setRecordingPayment] = useState<boolean>(false)
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<UnpaidInvoiceItem | null>(null)
  const [recordForm, setRecordForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_method: 'cash',
    amount: 0,
    reference_number: '',
    bank_name: '',
    notes: '',
  })

  const [linkModalOpen, setLinkModalOpen] = useState<boolean>(false)
  const [generatingLink, setGeneratingLink] = useState<boolean>(false)
  const [selectedInvoiceForLink, setSelectedInvoiceForLink] = useState<UnpaidInvoiceItem | null>(null)
  const [linkExpiryHours, setLinkExpiryHours] = useState<number>(48)
  const [generatedLinkResult, setGeneratedLinkResult] = useState<any>(null)

  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false)
  const [selectedPayment, setSelectedPayment] = useState<PaymentRecordItem | null>(null)
  const [voidReason, setVoidReason] = useState<string>('')
  const [voidingPayment, setVoidingPayment] = useState<boolean>(false)
  const [showVoidConfirm, setShowVoidConfirm] = useState<boolean>(false)

  // Load Metrics
  const loadMetrics = useCallback(async () => {
    try {
      setLoadingMetrics(true)
      const data = await paymentService.getDashboardMetrics()
      setMetrics(data)
    } catch (err: any) {
      console.error('Failed to load payment metrics', err)
    } finally {
      setLoadingMetrics(false)
    }
  }, [])

  // Load Payments Table
  const loadPayments = useCallback(async () => {
    try {
      setLoadingPayments(true)
      const filters: any = {
        page: page + 1,
        per_page: rowsPerPage,
      }
      if (searchTerm.trim()) filters.search_term = searchTerm.trim()
      if (filterMethod !== 'all') filters.payment_method = filterMethod
      if (filterStatus !== 'all') filters.status = filterStatus
      if (dateFrom) filters.payment_date_from = dateFrom
      if (dateTo) filters.payment_date_to = dateTo

      // Tab-specific filters
      if (currentTab === 2) {
        // Online only
        filters.payment_method = 'upi,credit_card,debit_card'
      } else if (currentTab === 3) {
        // Offline only
        filters.payment_method = 'cash,cheque,bank_transfer'
      }

      const result = await paymentService.listPayments(filters)
      setPayments(result.payments || [])
      setTotalPayments(result.total || 0)
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to fetch payments', { variant: 'error' })
    } finally {
      setLoadingPayments(false)
    }
  }, [page, rowsPerPage, searchTerm, filterMethod, filterStatus, dateFrom, dateTo, currentTab, enqueueSnackbar])

  // Load Unpaid Invoices
  const loadUnpaidInvoices = useCallback(async () => {
    try {
      setLoadingUnpaid(true)
      const res = await paymentService.getUnpaidInvoices(unpaidSearch.trim() || undefined, 100)
      setUnpaidInvoices(res.invoices || [])
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to fetch pending invoices', { variant: 'error' })
    } finally {
      setLoadingUnpaid(false)
    }
  }, [unpaidSearch, enqueueSnackbar])

  useEffect(() => {
    loadMetrics()
  }, [loadMetrics])

  useEffect(() => {
    if (currentTab === 1) {
      loadUnpaidInvoices()
    } else {
      loadPayments()
    }
  }, [currentTab, loadPayments, loadUnpaidInvoices])

  // Razorpay Live Sync
  const handleSyncRazorpay = async () => {
    try {
      setSyncLoading(true)
      setSyncResult(null)
      setSyncModalOpen(true)
      const result = await paymentService.syncRazorpayPayments(100)
      setSyncResult(result)
      enqueueSnackbar(
        `Razorpay sync complete: ${result.new_payments_created} new payments recorded, ${result.invoices_reconciled} invoices updated!`,
        { variant: 'success' }
      )
      loadMetrics()
      loadPayments()
      if (currentTab === 1) loadUnpaidInvoices()
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to sync payments from Razorpay', { variant: 'error' })
    } finally {
      setSyncLoading(false)
    }
  }

  // Open Record Payment Modal
  const handleOpenRecordModal = (invoice?: UnpaidInvoiceItem) => {
    if (unpaidInvoices.length === 0) {
      loadUnpaidInvoices()
    }
    if (invoice) {
      setSelectedInvoiceForPayment(invoice)
      setRecordForm({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        amount: invoice.outstanding_amount,
        reference_number: '',
        bank_name: '',
        notes: `Payment for invoice ${invoice.invoice_number}`,
      })
    } else {
      setSelectedInvoiceForPayment(null)
      setRecordForm({
        payment_date: new Date().toISOString().split('T')[0],
        payment_method: 'cash',
        amount: 0,
        reference_number: '',
        bank_name: '',
        notes: '',
      })
    }
    setRecordModalOpen(true)
  }

  // Submit Record Payment
  const handleSubmitRecordPayment = async () => {
    if (!selectedInvoiceForPayment) {
      enqueueSnackbar('Please select an invoice', { variant: 'warning' })
      return
    }
    if (recordForm.amount <= 0) {
      enqueueSnackbar('Payment amount must be greater than 0', { variant: 'warning' })
      return
    }
    if (recordForm.amount > selectedInvoiceForPayment.outstanding_amount) {
      enqueueSnackbar(`Amount cannot exceed outstanding balance of ₹${selectedInvoiceForPayment.outstanding_amount.toLocaleString('en-IN')}`, {
        variant: 'warning',
      })
      return
    }

    try {
      setRecordingPayment(true)
      await paymentService.recordInvoicePayment({
        invoice_id: selectedInvoiceForPayment.id,
        payment_date: recordForm.payment_date,
        payment_method: recordForm.payment_method,
        amount: recordForm.amount,
        reference_number: recordForm.reference_number || undefined,
        bank_name: recordForm.bank_name || undefined,
        notes: recordForm.notes || undefined,
      })

      enqueueSnackbar('Payment recorded and reconciled successfully!', { variant: 'success' })
      setRecordModalOpen(false)
      loadMetrics()
      loadPayments()
      loadUnpaidInvoices()
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to record payment', { variant: 'error' })
    } finally {
      setRecordingPayment(false)
    }
  }

  // Open Generate Payment Link Modal
  const handleOpenLinkModal = (invoice?: UnpaidInvoiceItem) => {
    if (unpaidInvoices.length === 0) {
      loadUnpaidInvoices()
    }
    setSelectedInvoiceForLink(invoice || null)
    setGeneratedLinkResult(null)
    setLinkExpiryHours(48)
    setLinkModalOpen(true)
  }

  // Submit Generate Link
  const handleGeneratePaymentLink = async () => {
    if (!selectedInvoiceForLink) {
      enqueueSnackbar('Please select an invoice', { variant: 'warning' })
      return
    }
    try {
      setGeneratingLink(true)
      const res = await paymentService.generatePaymentLink({
        invoice_id: selectedInvoiceForLink.id,
        custom_expiry_hours: linkExpiryHours,
      })
      setGeneratedLinkResult(res)
      enqueueSnackbar(`Razorpay payment link generated: ${res.payment_link_url || res.short_url}`, { variant: 'success' })
      loadMetrics()
      loadUnpaidInvoices()
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to generate payment link', { variant: 'error' })
    } finally {
      setGeneratingLink(false)
    }
  }

  // Cancel Payment Link
  const handleCancelPaymentLink = async (invoiceId: string, invoiceNumber: string) => {
    if (!window.confirm(`Are you sure you want to cancel the active Razorpay payment link for Invoice ${invoiceNumber}?`)) {
      return
    }
    try {
      await paymentService.cancelPaymentLink(invoiceId)
      enqueueSnackbar(`Payment link for invoice ${invoiceNumber} cancelled`, { variant: 'success' })
      loadUnpaidInvoices()
      loadMetrics()
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to cancel payment link', { variant: 'error' })
    }
  }

  // Copy helper
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    enqueueSnackbar(`${label} copied to clipboard!`, { variant: 'info' })
  }

  // WhatsApp Share Link
  const handleShareWhatsApp = (invoice: UnpaidInvoiceItem) => {
    const link = invoice.razorpay_payment_link_url || generatedLinkResult?.payment_link_url
    if (!link) {
      enqueueSnackbar('No active payment link available. Please generate one first.', { variant: 'warning' })
      return
    }
    const cleanPhone = (invoice.customer_phone || '').replace(/\D/g, '')
    const phoneWithCountry = cleanPhone.length === 10 ? `91${cleanPhone}` : cleanPhone

    const message = `Namaste ${invoice.customer_name} ji,\n\nGreetings from Dhanshika Exports! Here is the secure Razorpay payment link for your Invoice *#${invoice.invoice_number}* (Balance Due: *₹${invoice.outstanding_amount.toLocaleString('en-IN')}*):\n\n👉 ${link}\n\nYou can pay instantly via UPI, Cards, or Netbanking. Please let us know once completed.\n\nDhanshika Exports`
    const url = `https://wa.me/${phoneWithCountry}?text=${encodeURIComponent(message)}`
    window.open(url, '_blank')
  }

  // Capture authorized Razorpay payment
  const handleCapturePayment = async (razorpayPaymentId: string) => {
    try {
      const res = await paymentService.captureRazorpayPayment(razorpayPaymentId)
      enqueueSnackbar(res.message || `Payment ${razorpayPaymentId} captured!`, { variant: 'success' })
      loadMetrics()
      if (currentTab === 1) loadUnpaidInvoices()
      else loadPayments()
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to capture payment', { variant: 'error' })
    }
  }

  // Void Payment
  const handleVoidPayment = async () => {
    if (!selectedPayment) return
    if (!voidReason.trim()) {
      enqueueSnackbar('Please provide a reason for voiding this payment', { variant: 'warning' })
      return
    }
    try {
      setVoidingPayment(true)
      await paymentService.voidPayment(selectedPayment.id, voidReason.trim())
      enqueueSnackbar(`Payment ${selectedPayment.payment_number} voided and invoice balance reversed!`, { variant: 'success' })
      setDetailModalOpen(false)
      setShowVoidConfirm(false)
      setVoidReason('')
      loadMetrics()
      loadPayments()
      loadUnpaidInvoices()
    } catch (err: any) {
      enqueueSnackbar(err.response?.data?.detail || 'Failed to void payment', { variant: 'error' })
    } finally {
      setVoidingPayment(false)
    }
  }

  // Method Chip Color
  const getMethodChip = (method: string) => {
    const m = (method || '').toLowerCase()
    if (m === 'upi') return <Chip size="small" label="UPI" color="primary" variant="outlined" sx={{ fontWeight: 600 }} />
    if (m === 'credit_card' || m === 'card') return <Chip size="small" label="Credit Card" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
    if (m === 'debit_card') return <Chip size="small" label="Debit Card" color="info" variant="outlined" sx={{ fontWeight: 600 }} />
    if (m === 'bank_transfer' || m === 'neft' || m === 'rtgs') return <Chip size="small" label="Bank / NEFT" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
    if (m === 'cheque') return <Chip size="small" label="Cheque" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
    if (m === 'cash') return <Chip size="small" label="Cash" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
    return <Chip size="small" label={method.toUpperCase()} variant="outlined" />
  }

  // Status Chip
  const getStatusChip = (status: string) => {
    const s = (status || '').toLowerCase()
    if (s === 'completed' || s === 'paid') return <Chip size="small" label="Completed" color="success" sx={{ fontWeight: 600 }} />
    if (s === 'partially_paid' || s === 'partial') return <Chip size="small" label="Partial" color="warning" sx={{ fontWeight: 600 }} />
    if (s === 'pending' || s === 'created') return <Chip size="small" label="Pending" color="default" sx={{ fontWeight: 600 }} />
    if (s === 'failed' || s === 'voided' || s === 'cancelled') return <Chip size="small" label={s.toUpperCase()} color="error" sx={{ fontWeight: 600 }} />
    return <Chip size="small" label={status} />
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header & Main Actions */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ color: 'text.primary', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PaymentsIcon fontSize="large" color="primary" />
            Payments & Collections Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Centralized hub for Razorpay sync, instant payment links, offline recording, and reconciliation.
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Tooltip title="Pull and auto-reconcile all captured payments directly from Razorpay API">
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SyncIcon />}
              onClick={handleSyncRazorpay}
              disabled={syncLoading}
              sx={{ fontWeight: 600 }}
            >
              {syncLoading ? 'Syncing...' : 'Sync Razorpay'}
            </Button>
          </Tooltip>

          <Button
            variant="outlined"
            color="secondary"
            startIcon={<LinkIcon />}
            onClick={() => handleOpenLinkModal()}
            sx={{ fontWeight: 600 }}
          >
            Create Payment Link
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenRecordModal()}
            sx={{ fontWeight: 600, boxShadow: 2 }}
          >
            Record Payment
          </Button>

          <Tooltip title="Razorpay API & Webhook Configuration">
            <IconButton onClick={() => navigate('/admin/integrations/razorpay')} color="inherit">
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Metric KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Total Collections */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderRadius: 2, borderLeft: '5px solid #2e7d32', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Total Collections
                </Typography>
                <TrendingUpIcon color="success" fontSize="small" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="success.main">
                {loadingMetrics ? <CircularProgress size={20} /> : `₹${(metrics?.total_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {metrics?.total_payments_count || 0} recorded payments
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Razorpay / Online */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderRadius: 2, borderLeft: '5px solid #0288d1', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Razorpay / Online
                </Typography>
                <CardIcon color="info" fontSize="small" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="info.main">
                {loadingMetrics ? <CircularProgress size={20} /> : `₹${(metrics?.online_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                UPI, Cards & Netbanking
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Offline / Bank */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderRadius: 2, borderLeft: '5px solid #7b1fa2', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Bank & Offline
                </Typography>
                <WalletIcon color="secondary" fontSize="small" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="secondary.main">
                {loadingMetrics ? <CircularProgress size={20} /> : `₹${(metrics?.offline_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Cash, Cheque & NEFT
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Collections */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderRadius: 2, borderLeft: '5px solid #00796b', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Today's Collected
                </Typography>
                <SuccessIcon color="primary" fontSize="small" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                {loadingMetrics ? <CircularProgress size={20} /> : `₹${(metrics?.today_collected || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Real-time today's total
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Outstanding */}
        <Grid item xs={12} sm={6} md={2.4}>
          <Card elevation={2} sx={{ borderRadius: 2, borderLeft: '5px solid #d32f2f', height: '100%' }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
                  Outstanding Due
                </Typography>
                <WarningIcon color="error" fontSize="small" />
              </Box>
              <Typography variant="h5" fontWeight={700} color="error.main">
                {loadingMetrics ? <CircularProgress size={20} /> : `₹${(metrics?.total_outstanding || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {metrics?.unpaid_invoices_count || 0} unpaid invoices ({metrics?.active_links_count || 0} active links)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Tabs Container */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={currentTab}
            onChange={(_, val) => {
              setCurrentTab(val)
              setPage(0)
            }}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="All Transactions" sx={{ fontWeight: 600 }} />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Unpaid Invoices & Payment Links</span>
                  {metrics && metrics.unpaid_invoices_count > 0 && (
                    <Chip size="small" label={metrics.unpaid_invoices_count} color="error" sx={{ height: 20, fontSize: '0.75rem' }} />
                  )}
                </Box>
              }
              sx={{ fontWeight: 600 }}
            />
            <Tab label="Online (Razorpay / UPI)" sx={{ fontWeight: 600 }} />
            <Tab label="Bank & Offline" sx={{ fontWeight: 600 }} />
          </Tabs>

          <IconButton
            onClick={() => {
              loadMetrics()
              if (currentTab === 1) loadUnpaidInvoices()
              else loadPayments()
            }}
            title="Refresh Table"
          >
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* TAB 0, 2, 3: Transactions Tables */}
        {currentTab !== 1 && (
          <Box sx={{ p: 2 }}>
            {/* Filter Bar */}
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search Payment #, Ref / UTR, Notes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setPage(0)
                      loadPayments()
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {currentTab === 0 && (
                <Grid item xs={6} sm={2.5} md={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Method"
                    value={filterMethod}
                    onChange={(e) => {
                      setFilterMethod(e.target.value)
                      setPage(0)
                    }}
                  >
                    <MenuItem value="all">All Methods</MenuItem>
                    <MenuItem value="cash">Cash</MenuItem>
                    <MenuItem value="upi">UPI</MenuItem>
                    <MenuItem value="bank_transfer">Bank Transfer / NEFT</MenuItem>
                    <MenuItem value="cheque">Cheque</MenuItem>
                    <MenuItem value="credit_card">Credit Card</MenuItem>
                    <MenuItem value="debit_card">Debit Card</MenuItem>
                  </TextField>
                </Grid>
              )}

              <Grid item xs={6} sm={2.5} md={2}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Status"
                  value={filterStatus}
                  onChange={(e) => {
                    setFilterStatus(e.target.value)
                    setPage(0)
                  }}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="failed">Failed / Voided</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="From Date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={6} sm={2} md={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="To Date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={2} md={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  size="medium"
                  startIcon={<FilterIcon />}
                  onClick={() => {
                    setPage(0)
                    loadPayments()
                  }}
                >
                  Apply
                </Button>
              </Grid>
            </Grid>

            {/* Payments Table */}
            <TableContainer sx={{ minHeight: 350 }}>
              <Table size="medium">
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Payment #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer / Invoice</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Reference / UTR / Razorpay ID</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Amount</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingPayments ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Loading payments...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No payments found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Try adjusting your filters or click "Sync Razorpay" to pull transactions from Razorpay.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((p) => {
                      const ref = p.reference_number || p.razorpay_payment_id || '-'
                      return (
                        <TableRow key={p.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ReceiptIcon fontSize="small" color="action" />
                              <span>{p.payment_number}</span>
                            </Box>
                          </TableCell>
                          <TableCell>{p.payment_date || (p.created_at ? p.created_at.split('T')[0] : '-')}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {p.customer_name || p.bank_name || 'External / Unlinked'}
                            </Typography>
                            {p.invoice_number ? (
                              <Typography variant="caption" color="text.secondary">
                                Invoice: {p.invoice_number}
                              </Typography>
                            ) : (
                              <Typography variant="caption" color="warning.main">
                                No Invoice (External)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{getMethodChip(p.payment_method)}</TableCell>
                          <TableCell>
                            {ref !== '-' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                  {ref}
                                </Typography>
                                <Tooltip title="Copy Reference">
                                  <IconButton size="small" onClick={() => handleCopy(ref, 'Reference number')}>
                                    <CopyIcon sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                            {p.bank_name && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Bank: {p.bank_name}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: 'success.main' }}>
                            ₹{(p.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </TableCell>
                          <TableCell align="center">{getStatusChip(p.status)}</TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="View Details">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => {
                                    setSelectedPayment(p)
                                    setShowVoidConfirm(false)
                                    setVoidReason('')
                                    setDetailModalOpen(true)
                                  }}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {p.status === 'completed' && (
                                <Tooltip title="Void Payment">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => {
                                      setSelectedPayment(p)
                                      setShowVoidConfirm(true)
                                      setVoidReason('')
                                      setDetailModalOpen(true)
                                    }}
                                  >
                                    <VoidIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalPayments}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </Box>
        )}

        {/* TAB 1: Unpaid Invoices & Payment Links View */}
        {currentTab === 1 && (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                sx={{ width: 360 }}
                placeholder="Search by customer name, phone, invoice #..."
                value={unpaidSearch}
                onChange={(e) => setUnpaidSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') loadUnpaidInvoices()
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: unpaidSearch && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setUnpaidSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadUnpaidInvoices}>
                Refresh
              </Button>
            </Box>

            <TableContainer sx={{ minHeight: 350 }}>
              <Table size="medium">
                <TableHead sx={{ backgroundColor: 'action.hover' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Invoice #</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Customer Details</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Date / Due</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Total (₹)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Paid (₹)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Balance Due (₹)</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Razorpay Link Status</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingUnpaid ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Loading pending invoices...
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : unpaidInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <SuccessIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h6" color="text.primary">
                          All caught up!
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          There are currently no unpaid invoices pending collection.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    unpaidInvoices.map((inv) => {
                      const hasActiveLink = inv.razorpay_payment_link_url && inv.razorpay_payment_link_status === 'created'
                      return (
                        <TableRow key={inv.id} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{inv.invoice_number}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {inv.customer_name}
                            </Typography>
                            {inv.customer_phone && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                📞 {inv.customer_phone}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{inv.invoice_date || '-'}</Typography>
                            {inv.due_date && (
                              <Typography variant="caption" color="error.main" display="block">
                                Due: {inv.due_date}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">₹{inv.total_amount.toLocaleString('en-IN')}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main' }}>
                            ₹{inv.paid_amount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: 'error.main', fontSize: '1rem' }}>
                            ₹{inv.outstanding_amount.toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell align="center">
                            {inv.razorpay_payment_link_id ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={`Link: ${(inv.razorpay_payment_link_status || 'Active').toUpperCase()}`}
                                  color={hasActiveLink ? 'info' : 'default'}
                                  variant="outlined"
                                  sx={{ fontWeight: 600 }}
                                />
                                {inv.razorpay_payment_link_url && (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Tooltip title="Copy Razorpay Link">
                                      <IconButton size="small" onClick={() => handleCopy(inv.razorpay_payment_link_url!, 'Payment link')}>
                                        <CopyIcon sx={{ fontSize: '0.9rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Open in browser">
                                      <IconButton size="small" onClick={() => window.open(inv.razorpay_payment_link_url, '_blank')}>
                                        <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                )}
                              </Box>
                            ) : (
                              <Chip size="small" label="Not Generated" variant="outlined" color="default" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              {/* 1-Click WhatsApp Share */}
                              {hasActiveLink && (
                                <Tooltip title="Share Payment Link on WhatsApp">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    startIcon={<WhatsAppIcon />}
                                    onClick={() => handleShareWhatsApp(inv)}
                                  >
                                    WhatsApp
                                  </Button>
                                </Tooltip>
                              )}

                              {/* Generate or Re-generate Link */}
                              {!hasActiveLink ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<LinkIcon />}
                                  onClick={() => handleOpenLinkModal(inv)}
                                >
                                  Create Link
                                </Button>
                              ) : (
                                <Tooltip title="Cancel Payment Link">
                                  <Button
                                    size="small"
                                    color="error"
                                    variant="text"
                                    onClick={() => handleCancelPaymentLink(inv.id, inv.invoice_number)}
                                  >
                                    Cancel Link
                                  </Button>
                                </Tooltip>
                              )}

                              {/* Record Offline Payment */}
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenRecordModal(inv)}
                              >
                                Record
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>

      {/* ========================================================================= */}
      {/* MODAL 1: RECORD PAYMENT MODAL                                             */}
      {/* ========================================================================= */}
      <Dialog open={recordModalOpen} onClose={() => setRecordModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentsIcon color="primary" /> Record Payment Against Invoice
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {/* Invoice Autocomplete */}
            <Autocomplete
              options={unpaidInvoices}
              getOptionLabel={(option) =>
                `${option.invoice_number} - ${option.customer_name} (Balance Due: ₹${option.outstanding_amount.toLocaleString('en-IN')})`
              }
              value={selectedInvoiceForPayment}
              onChange={(_, newValue) => {
                setSelectedInvoiceForPayment(newValue)
                if (newValue) {
                  setRecordForm((prev) => ({
                    ...prev,
                    amount: newValue.outstanding_amount,
                    notes: `Payment for invoice ${newValue.invoice_number}`,
                  }))
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Invoice *"
                  placeholder="Type invoice number or customer name"
                  required
                />
              )}
            />

            {/* Selected Invoice Overview */}
            {selectedInvoiceForPayment && (
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Customer</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedInvoiceForPayment.customer_name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Total Invoice Amount</Typography>
                    <Typography variant="body2">₹{selectedInvoiceForPayment.total_amount.toLocaleString('en-IN')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Paid So Far</Typography>
                    <Typography variant="body2" color="success.main">₹{selectedInvoiceForPayment.paid_amount.toLocaleString('en-IN')}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Outstanding Balance</Typography>
                    <Typography variant="body2" color="error.main" fontWeight={700}>₹{selectedInvoiceForPayment.outstanding_amount.toLocaleString('en-IN')}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Amount (₹) *"
                  type="number"
                  value={recordForm.amount}
                  onChange={(e) => setRecordForm({ ...recordForm, amount: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.01 }}
                  required
                  helperText={selectedInvoiceForPayment ? `Max: ₹${selectedInvoiceForPayment.outstanding_amount.toLocaleString('en-IN')}` : ''}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Payment Date *"
                  value={recordForm.payment_date}
                  onChange={(e) => setRecordForm({ ...recordForm, payment_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Payment Method *"
                  value={recordForm.payment_method}
                  onChange={(e) => setRecordForm({ ...recordForm, payment_method: e.target.value })}
                  required
                >
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="upi">UPI</MenuItem>
                  <MenuItem value="bank_transfer">Bank Transfer / NEFT / RTGS</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="credit_card">Credit Card</MenuItem>
                  <MenuItem value="debit_card">Debit Card</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Reference / UTR / Cheque #"
                  placeholder="e.g. UTR123456, Cheque #0045"
                  value={recordForm.reference_number}
                  onChange={(e) => setRecordForm({ ...recordForm, reference_number: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              label="Bank Name (Optional)"
              placeholder="e.g. HDFC Bank, SBI, ICICI"
              value={recordForm.bank_name}
              onChange={(e) => setRecordForm({ ...recordForm, bank_name: e.target.value })}
            />

            <TextField
              fullWidth
              multiline
              rows={2}
              label="Payment Notes"
              placeholder="Add any internal remarks or collection context"
              value={recordForm.notes}
              onChange={(e) => setRecordForm({ ...recordForm, notes: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRecordModalOpen(false)} disabled={recordingPayment}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitRecordPayment}
            disabled={recordingPayment || !selectedInvoiceForPayment || recordForm.amount <= 0}
            startIcon={recordingPayment ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {recordingPayment ? 'Recording...' : 'Record Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 2: GENERATE PAYMENT LINK MODAL                                      */}
      {/* ========================================================================= */}
      <Dialog open={linkModalOpen} onClose={() => setLinkModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon color="secondary" /> Generate Razorpay Payment Link
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <Autocomplete
              options={unpaidInvoices}
              getOptionLabel={(option) =>
                `${option.invoice_number} - ${option.customer_name} (Balance: ₹${option.outstanding_amount.toLocaleString('en-IN')})`
              }
              value={selectedInvoiceForLink}
              onChange={(_, newValue) => {
                setSelectedInvoiceForLink(newValue)
                setGeneratedLinkResult(null)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Invoice *"
                  placeholder="Search invoice number or customer"
                  required
                />
              )}
            />

            <TextField
              select
              fullWidth
              label="Link Expiry Duration"
              value={linkExpiryHours}
              onChange={(e) => setLinkExpiryHours(parseInt(e.target.value, 10))}
            >
              <MenuItem value={24}>24 Hours (1 Day)</MenuItem>
              <MenuItem value={48}>48 Hours (2 Days)</MenuItem>
              <MenuItem value={72}>72 Hours (3 Days)</MenuItem>
              <MenuItem value={168}>7 Days (1 Week)</MenuItem>
              <MenuItem value={720}>30 Days (1 Month)</MenuItem>
            </TextField>

            {generatedLinkResult && (
              <Alert severity="success" icon={<SuccessIcon />} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Payment Link Created Successfully!
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all', mb: 1.5, fontFamily: 'monospace' }}>
                  {generatedLinkResult.payment_link_url || generatedLinkResult.short_url}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<CopyIcon />}
                    onClick={() => handleCopy(generatedLinkResult.payment_link_url || generatedLinkResult.short_url, 'Payment Link')}
                  >
                    Copy Link
                  </Button>
                  {selectedInvoiceForLink && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => handleShareWhatsApp(selectedInvoiceForLink)}
                    >
                      Send WhatsApp
                    </Button>
                  )}
                </Stack>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setLinkModalOpen(false)}>Close</Button>
          {!generatedLinkResult && (
            <Button
              variant="contained"
              color="secondary"
              onClick={handleGeneratePaymentLink}
              disabled={generatingLink || !selectedInvoiceForLink}
              startIcon={generatingLink ? <CircularProgress size={16} /> : <LinkIcon />}
            >
              {generatingLink ? 'Generating Link...' : 'Create Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 3: RAZORPAY LIVE SYNC RESULTS DIALOG                                */}
      {/* ========================================================================= */}
      <Dialog open={syncModalOpen} onClose={() => setSyncModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <SyncIcon color="primary" /> Razorpay Payment Synchronization Results
        </DialogTitle>
        <DialogContent dividers>
          {syncLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={40} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Querying Razorpay API & Reconciling Invoices...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Checking captured payments, verifying notes, and updating invoice balances.
              </Typography>
            </Box>
          ) : syncResult ? (
            <Stack spacing={2.5}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Transactions Scanned</Typography>
                    <Typography variant="h5" fontWeight={700}>{syncResult.synced_count}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'success.50' }}>
                    <Typography variant="caption" color="success.dark">New Payments Created</Typography>
                    <Typography variant="h5" fontWeight={700} color="success.main">
                      {syncResult.new_payments_created}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: 'primary.50' }}>
                    <Typography variant="caption" color="primary.dark">Invoices Reconciled</Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                      {syncResult.invoices_reconciled}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Amount Reconciled</Typography>
                    <Typography variant="h6" fontWeight={700} color="success.main">
                      ₹{syncResult.total_synced_amount.toLocaleString('en-IN')}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" fontWeight={700}>
                Synced Transactions Details:
              </Typography>

              <TableContainer sx={{ maxHeight: 350 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Razorpay Payment ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Method / UPI ID</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Bank RRN / Contact</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Invoice Match</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Sync Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncResult.items.map((item: any, idx) => (
                      <TableRow key={idx}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{item.payment_id}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>₹{item.amount.toLocaleString('en-IN')}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {(item.method || 'online').toUpperCase()}
                          </Typography>
                          {item.vpa && (
                            <Typography variant="caption" color="primary.main" display="block">
                              VPA: {item.vpa}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {item.rrn && (
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                              RRN: {item.rrn}
                            </Typography>
                          )}
                          {item.customer_contact && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              📞 {item.customer_contact}
                            </Typography>
                          )}
                          {!item.rrn && !item.customer_contact && (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          )}
                        </TableCell>
                        <TableCell>{item.invoice_number || <span style={{ color: '#999' }}>Unlinked</span>}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <Chip
                              size="small"
                              label={item.status.replace(/_/g, ' ').toUpperCase()}
                              color={
                                item.status === 'reconciled_and_recorded'
                                  ? 'success'
                                  : item.status === 'unlinked_recorded'
                                  ? 'warning'
                                  : item.status === 'already_recorded'
                                  ? 'info'
                                  : item.status === 'authorized'
                                  ? 'warning'
                                  : 'default'
                              }
                              sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                            />
                            {item.status === 'authorized' && (
                              <Button
                                size="small"
                                variant="contained"
                                color="warning"
                                sx={{ fontSize: '0.7rem', py: 0.25, px: 1, minWidth: 0 }}
                                onClick={() => handleCapturePayment(item.payment_id)}
                              >
                                Capture
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          ) : (
            <Alert severity="warning">No sync data available.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="contained" onClick={() => setSyncModalOpen(false)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================================= */}
      {/* MODAL 4: PAYMENT DETAILS & RECEIPT MODAL                                  */}
      {/* ========================================================================= */}
      <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Payment Receipt Details</span>
          {selectedPayment && getStatusChip(selectedPayment.status)}
        </DialogTitle>
        <DialogContent dividers>
          {selectedPayment && (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Payment Number</Typography>
                    <Typography variant="body1" fontWeight={700}>{selectedPayment.payment_number}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Payment Date</Typography>
                    <Typography variant="body1">{selectedPayment.payment_date || selectedPayment.created_at?.split('T')[0]}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedPayment.customer_name || 'Customer'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Invoice Number</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedPayment.invoice_number || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Payment Method</Typography>
                    <Box sx={{ mt: 0.5 }}>{getMethodChip(selectedPayment.payment_method)}</Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Amount Received</Typography>
                    <Typography variant="h6" fontWeight={700} color="success.main">
                      ₹{(selectedPayment.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  {(selectedPayment.reference_number || selectedPayment.razorpay_payment_id) && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Reference / UTR / Razorpay ID</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {selectedPayment.reference_number || selectedPayment.razorpay_payment_id}
                      </Typography>
                    </Grid>
                  )}
                  {selectedPayment.bank_name && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Bank / Gateway</Typography>
                      <Typography variant="body2">{selectedPayment.bank_name}</Typography>
                    </Grid>
                  )}
                  {selectedPayment.notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography variant="body2">{selectedPayment.notes}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* Void Payment Confirmation Box */}
              {showVoidConfirm && (
                <Paper variant="outlined" sx={{ p: 2, borderColor: 'error.main', bgcolor: 'error.50' }}>
                  <Typography variant="subtitle2" color="error.main" fontWeight={700} gutterBottom>
                    Confirm Void Payment
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Voiding this payment will reverse its effect on the invoice balance and customer ledger.
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Reason for Voiding *"
                    placeholder="e.g. Duplicate entry, Cheque bounced, Wrong amount"
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    sx={{ mb: 1.5 }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" onClick={() => setShowVoidConfirm(false)}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="error"
                      onClick={handleVoidPayment}
                      disabled={voidingPayment || !voidReason.trim()}
                      startIcon={voidingPayment ? <CircularProgress size={14} /> : <VoidIcon />}
                    >
                      {voidingPayment ? 'Voiding...' : 'Confirm Void'}
                    </Button>
                  </Stack>
                </Paper>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {selectedPayment?.status === 'completed' && !showVoidConfirm && (
            <Button color="error" startIcon={<VoidIcon />} onClick={() => setShowVoidConfirm(true)}>
              Void Payment
            </Button>
          )}
          <Button variant="contained" onClick={() => setDetailModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PaymentsDashboardPage
